<template>
    <div class="policies">
        <div class="table">
            <div class="table__header box box--default">
                <div class="table__label table__label--small">
                    {{
                        $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"date"}`)
                    }}
                </div>
                <div class="table__label table__label--small">
                    Status
                </div>
                <div class="table__label table__label--medium">
                    {{
                        $t(
                            `accountDetails.${"customerLeadsTable"}.${"header"}.${"category"}`
                        )
                    }}
                </div>
                <div class="table__label table__label--small">
                    {{
                        $t(
                            `accountDetails.${"customerLeadsTable"}.${"header"}.${"referal"}`
                        )
                    }}
                </div>
                <div class="table__label table__label--medium">
                    {{
                        $t(
                            `accountDetails.${"customerLeadsTable"}.${"header"}.${"agent"}`
                        )
                    }}
                </div>
                <div class="table__label table__label--small">
                    {{
                        $t(
                            `accountDetails.${"customerLeadsTable"}.${"header"}.${"contact"}`
                        )
                    }}
                </div>
                <div class="table__label table__label--small">
                    {{
                        $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"deadline"}`)
                    }}
                </div>
                <div class="table__label table__label--large">
                    {{
                        $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"text"}`)
                    }}
                </div>
                <div class="table__label table__label--small  "></div>
            </div>

            <div class="table__body">
                <Loading v-if="detailsLoading" />
                <template v-else>
                    <div v-if="!leadsData.length" class="table__no-data">
                        <NoData :type="'customer-leads'" />
                    </div>
                    <template v-else>
                        <Lead v-for="(lead, index) in leadsData" :key="lead.id" :lead="lead" :index="index" />
                    </template>
                </template>
            </div>
        </div>
        <Pagination :limit="3" :data="leadsPerPage" @pagination-change-page="getResults"></Pagination>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import NoData from "@/partials/NoData";
import Loading from "@/partials/Loading";
import Lead from "@/components/AccountDetails/Lead";

defineOptions({
    name: "CustomerLeadsTable"
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();

// State
const detailsLoading = ref(false);
const lastFetchedCustomerId = ref(null);
const lastFetchedPage = ref(null);

// Computed
const leadsPerPage = computed(() => store.getters.leadsPerPage);
const customerDetails = computed(() => store.getters.customerDetails);
const customerId = computed(() => store.getters.customerDetailsId);
const leadsData = computed(() => leadsPerPage.value.data || []);

// Methods
const loadLeads = async () => {
    const page = Number(route.query.page) || 1;

    // Only fetch if customer or page changed
    if (lastFetchedCustomerId.value === customerId.value && lastFetchedPage.value === page) {
        return;
    }

    detailsLoading.value = true;
    try {
        if (customerId.value) {
            await store.dispatch("fetchLeads", { customerId: customerId.value, page });
            lastFetchedCustomerId.value = customerId.value;
            lastFetchedPage.value = page;

            if (route.params.entity === "lead" && leadsData.value.length === 0) {
                store.commit("setShowAddLeadModal", true);
            }
        }
    } catch (error) {
        console.error(error);
    } finally {
        detailsLoading.value = false;
    }
};

const getResults = (paginationPage) => {
    if (Number(route.query.page) === Number(paginationPage)) return;

    router.replace({
        query: { ...route.query, page: paginationPage },
    }).catch(() => { });
};

// Watchers
watch(
    () => route.query,
    (query, oldQuery) => {
        if (customerId.value && oldQuery && query.page !== oldQuery.page) {
            if (!query.page) {
                router.replace({ query: { ...query, page: 1 } });
                return;
            }
            loadLeads();
        }
    },
    { deep: true }
);

// Lifecycle
onMounted(async () => {
    if (!(proxy.$checkRolePermission('customer_access') || proxy.$checkRolePermission('customer_show'))) {
        router.push('/dashboard');
        return;
    }

    const initLogic = async () => {
        const query = route.query;
        if (!query.page) {
            router.replace({ query: { ...query, page: 1 } });
        } else {
            await loadLeads();
        }
    };

    if (customerDetails.value?.id) {
        await initLogic();
    } else {
        // Watch for customerDetails initialization
        const unwatch = watch(
            () => customerDetails.value,
            async (newVal) => {
                if (newVal?.id) {
                    unwatch();
                    await initLogic();
                }
            }
        );
    }
});
</script>

<style scoped lang="scss">
.policies .table__label:first-of-type {
    padding: 2px 10px !important;
}
</style>
